/**
 * NotePickerContextMenu Component
 * 
 * Desktop context menu for selecting notes that appears on right-click.
 * Similar to the mobile NotePickerMenu but optimized for desktop use.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './NotePickerContextMenu.css';

export interface NotePickerContextMenuProps {
  /** Current note value (e.g., "C4") */
  currentNote: string;
  /** Track index for display */
  trackIndex: number;
  /** Step index for display */
  stepIndex: number;
  /** Callback when note is selected */
  onNoteSelect: (note: string) => void;
  /** Callback when menu is closed */
  onClose: () => void;
}

const NOTE_LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const OCTAVES = [1, 2, 3, 4, 5, 6, 7];

/**
 * NotePickerContextMenu Component
 * 
 * Displays a context menu for selecting notes with:
 * - Note letter (C, D, E, F, G, A, B)
 * - Sharp/Natural toggle
 * - Octave selection
 * 
 * Replaces the prompt dialog for better desktop UX
 */
export const NotePickerContextMenu: React.FC<NotePickerContextMenuProps> = ({
  currentNote,
  trackIndex,
  stepIndex,
  onNoteSelect,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Parse current note
  const parseNote = (note: string): { letter: string; sharp: boolean; octave: number } => {
    const match = note.match(/^([A-G])(#?)(\d+)$/);
    if (!match) return { letter: 'C', sharp: false, octave: 4 };
    
    return {
      letter: match[1] || 'C',
      sharp: match[2] === '#',
      octave: parseInt(match[3] || '4', 10),
    };
  };

  const parsed = parseNote(currentNote);
  const [selectedLetter, setSelectedLetter] = useState(parsed.letter);
  const [isSharp, setIsSharp] = useState(parsed.sharp);
  const [selectedOctave, setSelectedOctave] = useState(parsed.octave);

  // Update preview when selections change
  const previewNote = `${selectedLetter}${isSharp ? '#' : ''}${selectedOctave}`;

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Add slight delay to prevent immediate close from the right-click event
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle confirm
  const handleConfirm = () => {
    onNoteSelect(previewNote);
    onClose();
  };

  // Handle quick select (click on note letter or octave)
  const handleQuickSelect = (letter: string, sharp: boolean, octave: number) => {
    const note = `${letter}${sharp ? '#' : ''}${octave}`;
    onNoteSelect(note);
    onClose();
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="note-picker-context-backdrop" onClick={onClose}>
        {/* Menu */}
        <div
          ref={menuRef}
          className="note-picker-context-menu"
          role="dialog"
          aria-modal="true"
          aria-labelledby="note-picker-context-title"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="note-picker-context-header">
            <div>
              <h3 id="note-picker-context-title">Select Note</h3>
              <p className="note-picker-context-subtitle">
                Track {trackIndex + 1}, Step {stepIndex + 1}
              </p>
            </div>
            <button
              className="note-picker-context-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Preview */}
          <div className="note-picker-context-preview">
            <span className="note-picker-context-preview-label">Preview:</span>
            <span className="note-picker-context-preview-note">{previewNote}</span>
          </div>

          {/* Note Letter Section */}
          <div className="note-picker-context-section">
            <div className="note-picker-context-section-label">Note Letter</div>
            <div className="note-picker-context-options">
              {NOTE_LETTERS.map((letter) => (
                <button
                  key={letter}
                  className={`note-picker-context-option ${selectedLetter === letter ? 'selected' : ''}`}
                  onClick={() => setSelectedLetter(letter)}
                  onDoubleClick={() => handleQuickSelect(letter, isSharp, selectedOctave)}
                  aria-pressed={selectedLetter === letter}
                  title="Double-click to apply"
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          {/* Sharp/Natural Section */}
          <div className="note-picker-context-section">
            <div className="note-picker-context-section-label">Accidental</div>
            <div className="note-picker-context-options">
              <button
                className={`note-picker-context-option ${!isSharp ? 'selected' : ''}`}
                onClick={() => setIsSharp(false)}
                aria-pressed={!isSharp}
              >
                ♮ Natural
              </button>
              <button
                className={`note-picker-context-option ${isSharp ? 'selected' : ''}`}
                onClick={() => setIsSharp(true)}
                aria-pressed={isSharp}
              >
                ♯ Sharp
              </button>
            </div>
          </div>

          {/* Octave Section */}
          <div className="note-picker-context-section">
            <div className="note-picker-context-section-label">Octave</div>
            <div className="note-picker-context-options note-picker-context-octaves">
              {OCTAVES.map((octave) => (
                <button
                  key={octave}
                  className={`note-picker-context-option ${selectedOctave === octave ? 'selected' : ''}`}
                  onClick={() => setSelectedOctave(octave)}
                  onDoubleClick={() => handleQuickSelect(selectedLetter, isSharp, octave)}
                  aria-pressed={selectedOctave === octave}
                  title="Double-click to apply"
                >
                  {octave}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="note-picker-context-actions">
            <button
              className="note-picker-context-button note-picker-context-button-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="note-picker-context-button note-picker-context-button-confirm"
              onClick={handleConfirm}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};
